import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMER_TYPE_MASTER_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

export default function CustomerTypeMaster() {
    const { fetch, create, update, remove } = useApiMethods();

    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // inline add
    const [newName, setNewName] = useState("");
    const [newIsCP, setNewIsCP] = useState(false);
    const [adding, setAdding] = useState(false);

    // inline edit
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editIsCP, setEditIsCP] = useState(false);

    const loadTypes = async () => {
        const res = await fetch(CUSTOMER_TYPE_MASTER_URL, {}, setLoading);
        if (res?.status === "success") setTypes(res.data || []);
    };

    useEffect(() => { loadTypes(); }, []);

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        const res = await create(CUSTOMER_TYPE_MASTER_URL, { name, is_channel_partner: newIsCP }, false, setAdding);
        if (res?.status === "success") {
            ToastMsg("Customer type added", "success");
            setNewName("");
            setNewIsCP(false);
            loadTypes();
        } else {
            ToastMsg(res?.message || "Failed to add", "error");
        }
    };

    const handleUpdate = async (id) => {
        const name = editName.trim();
        if (!name) return;
        const res = await update(CUSTOMER_TYPE_MASTER_URL, { id, name, is_channel_partner: editIsCP }, false, null);
        if (res?.status === "success") {
            ToastMsg("Updated", "success");
            setEditId(null);
            loadTypes();
        } else {
            ToastMsg(res?.message || "Failed to update", "error");
        }
    };

    const handleToggleActive = async (item) => {
        const res = await update(CUSTOMER_TYPE_MASTER_URL, {
            id: item.id,
            name: item.name,
            is_active: !item.is_active,
            is_channel_partner: item.is_channel_partner,
        }, false, null);
        if (res?.status === "success") {
            ToastMsg(item.is_active ? "Deactivated" : "Activated", "success");
            loadTypes();
        }
    };

    const handleToggleCP = async (item) => {
        const res = await update(CUSTOMER_TYPE_MASTER_URL, {
            id: item.id,
            name: item.name,
            is_active: item.is_active,
            is_channel_partner: !item.is_channel_partner,
        }, false, null);
        if (res?.status === "success") {
            ToastMsg(
                !item.is_channel_partner ? "Marked as Channel Partner" : "Marked as Prospect",
                "success"
            );
            loadTypes();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this customer type?")) return;
        const res = await remove(CUSTOMER_TYPE_MASTER_URL, { id }, null);
        if (res?.status === "success") {
            ToastMsg("Deleted", "success");
            loadTypes();
        } else {
            ToastMsg(res?.message || "Failed to delete", "error");
        }
    };

    return (
        <ContainerCard>
            <div className="p-4 max-w-lg">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Customer Type Master
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Toggle <span className="font-medium text-purple-600">Channel Partner</span> to classify a type as CP — all customers using that type will be treated as Channel Partners.
                </p>

                {/* Add new */}
                <div className="flex gap-2 mb-2">
                    <input
                        type="text"
                        placeholder="New customer type name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Button
                        onClick={handleAdd}
                        disabled={adding || !newName.trim()}
                        className="flex items-center gap-1"
                    >
                        <Plus className="h-4 w-4" />
                        Add
                    </Button>
                </div>
                {/* CP toggle for new entry */}
                <label className="flex items-center gap-2 mb-5 cursor-pointer select-none w-fit">
                    <div
                        onClick={() => setNewIsCP(!newIsCP)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${newIsCP ? "bg-purple-600" : "bg-gray-300 dark:bg-gray-600"}`}
                    >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${newIsCP ? "translate-x-4" : ""}`} />
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Channel Partner type</span>
                </label>

                {/* List */}
                {loading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
                ) : types.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No customer types yet.</p>
                ) : (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
                        {types.map((item) => (
                            <li key={item.id} className="flex items-center gap-2 px-3 py-2.5">
                                {editId === item.id ? (
                                    <>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleUpdate(item.id);
                                                if (e.key === "Escape") setEditId(null);
                                            }}
                                            className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm
                                                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {/* CP toggle in edit mode */}
                                        <label className="flex items-center gap-1 cursor-pointer select-none">
                                            <div
                                                onClick={() => setEditIsCP(!editIsCP)}
                                                className={`relative w-8 h-4 rounded-full transition-colors ${editIsCP ? "bg-purple-600" : "bg-gray-300 dark:bg-gray-600"}`}
                                            >
                                                <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${editIsCP ? "translate-x-4" : ""}`} />
                                            </div>
                                            <span className="text-xs text-gray-500">CP</span>
                                        </label>
                                        <button onClick={() => handleUpdate(item.id)} className="text-green-600 hover:text-green-800">
                                            <Check className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => setEditId(null)} className="text-gray-500 hover:text-gray-700">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className={`flex-1 text-sm ${item.is_active ? "text-gray-900 dark:text-white" : "text-gray-400 line-through"}`}>
                                            {item.name}
                                        </span>
                                        {/* Channel Partner badge / toggle */}
                                        <button
                                            onClick={() => handleToggleCP(item)}
                                            title={item.is_channel_partner ? "Click to mark as Prospect type" : "Click to mark as Channel Partner type"}
                                            className={`text-xs px-1.5 py-0.5 rounded transition-colors ${
                                                item.is_channel_partner
                                                    ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                                                    : "bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 hover:bg-purple-50 hover:text-purple-500"
                                            }`}
                                        >
                                            {item.is_channel_partner ? "CP" : "Prospect"}
                                        </button>
                                        <button
                                            onClick={() => handleToggleActive(item)}
                                            title={item.is_active ? "Deactivate" : "Activate"}
                                            className={`text-xs px-1.5 py-0.5 rounded ${
                                                item.is_active
                                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                            }`}
                                        >
                                            {item.is_active ? "Active" : "Inactive"}
                                        </button>
                                        <button
                                            onClick={() => { setEditId(item.id); setEditName(item.name); setEditIsCP(item.is_channel_partner); }}
                                            className="text-blue-500 hover:text-blue-700"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </ContainerCard>
    );
}
